import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Pen, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useEventTracking } from '@/hooks/useEventTracking';
import type { Announcement } from '@/types/announcement';

interface MomentsThatMatterPanelProps {
  announcements: Announcement[];
}

const panelCopy = {
  en: {
    title: 'Moments That Matter',
    writeMessage: 'Write a Message',
    viewTree: 'View Your Tree',
    writeFirst: 'Write Your First Message',
    starterInsight: 'Your first message plants the seed of your Living Tree.',
  },
  es: {
    title: 'Momentos Que Importan',
    writeMessage: 'Escribe un Mensaje',
    viewTree: 'Ver Tu Árbol',
    writeFirst: 'Escribe Tu Primer Mensaje',
    starterInsight: 'Tu primer mensaje planta la semilla de tu Árbol Viviente.',
  },
  fr: {
    title: 'Moments Qui Comptent',
    writeMessage: 'Écrire un Message',
    viewTree: 'Voir Votre Arbre',
    writeFirst: 'Écrire Votre Premier Message',
    starterInsight: 'Votre premier message plante la graine de votre Arbre Vivant.',
  },
  pt: {
    title: 'Momentos Que Importam',
    writeMessage: 'Escreva uma Mensagem',
    viewTree: 'Veja Sua Árvore',
    writeFirst: 'Escreva Sua Primeira Mensagem',
    starterInsight: 'Sua primeira mensagem planta a semente da sua Árvore Viva.',
  },
  ar: {
    title: 'لحظات مهمة',
    writeMessage: 'اكتب رسالة',
    viewTree: 'شاهد شجرتك',
    writeFirst: 'اكتب رسالتك الأولى',
    starterInsight: 'رسالتك الأولى تزرع بذرة شجرتك الحية.',
  },
  ja: {
    title: '大切な瞬間',
    writeMessage: 'メッセージを書く',
    viewTree: 'ツリーを見る',
    writeFirst: '最初のメッセージを書く',
    starterInsight: '最初のメッセージがリビングツリーの種を植えます。',
  },
  ko: {
    title: '소중한 순간들',
    writeMessage: '메시지 쓰기',
    viewTree: '나무 보기',
    writeFirst: '첫 메시지 쓰기',
    starterInsight: '첫 메시지가 리빙 트리의 씨앗을 심습니다.',
  },
  id: {
    title: 'Momen Yang Berarti',
    writeMessage: 'Tulis Pesan',
    viewTree: 'Lihat Pohon Anda',
    writeFirst: 'Tulis Pesan Pertama Anda',
    starterInsight: 'Pesan pertama Anda menanam benih Pohon Hidup Anda.',
  },
  hi: {
    title: 'महत्वपूर्ण पल',
    writeMessage: 'संदेश लिखें',
    viewTree: 'अपना पेड़ देखें',
    writeFirst: 'अपना पहला संदेश लिखें',
    starterInsight: 'आपका पहला संदेश आपके जीवित पेड़ का बीज बोता है।',
  },
};

function generateInsights(
  published: Announcement[],
  language: string
): string[] {
  const insights: string[] = [];
  const total = published.length;

  if (total === 0) return [];

  const now = new Date();
  const thisMonth = published.filter((a) => {
    const d = new Date(a.published_at || a.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const uniqueRecipients = new Set(published.map((a) => a.recipient_name.toLowerCase()));

  const relationshipCounts: Record<string, number> = {};
  published.forEach((a) => {
    relationshipCounts[a.relationship] = (relationshipCounts[a.relationship] || 0) + 1;
  });
  const topRelationship = Object.entries(relationshipCounts).sort((a, b) => b[1] - a[1])[0];

  const lastDate = published.reduce((latest, a) => {
    const d = new Date(a.published_at || a.created_at);
    return d > latest ? d : latest;
  }, new Date(0));
  const daysSinceLast = Math.floor((now.getTime() - lastDate.getTime()) / 86400000);

  const insightTemplates: Record<string, (v: Record<string, string | number>) => string> = {
    en: (v) => {
      const pool = [
        `You've expressed something meaningful to ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'person' : 'people'} this month.`,
        `Your Living Tree has grown to ${v.total} ${v.total === 1 ? 'moment' : 'moments'}.`,
        `You've written the most messages to ${v.topRelationship}.`,
        `It's been ${v.daysSinceLast} ${v.daysSinceLast === 1 ? 'day' : 'days'} since your last message.`,
        `You've shared gratitude with ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'person' : 'people'}.`,
      ];
      return pool[Number(v.index) % pool.length];
    },
    es: (v) => {
      const pool = [
        `Has expresado algo significativo a ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'persona' : 'personas'} este mes.`,
        `Tu Árbol Viviente ha crecido a ${v.total} ${v.total === 1 ? 'momento' : 'momentos'}.`,
        `Has escrito más mensajes a ${v.topRelationship}.`,
        `Han pasado ${v.daysSinceLast} ${v.daysSinceLast === 1 ? 'día' : 'días'} desde tu último mensaje.`,
        `Has compartido gratitud con ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'persona' : 'personas'}.`,
      ];
      return pool[Number(v.index) % pool.length];
    },
    fr: (v) => {
      const pool = [
        `Vous avez exprimé quelque chose de significatif à ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'personne' : 'personnes'} ce mois-ci.`,
        `Votre Arbre Vivant a grandi à ${v.total} ${v.total === 1 ? 'moment' : 'moments'}.`,
        `Vous avez écrit le plus de messages à ${v.topRelationship}.`,
        `Cela fait ${v.daysSinceLast} ${v.daysSinceLast === 1 ? 'jour' : 'jours'} depuis votre dernier message.`,
        `Vous avez partagé de la gratitude avec ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'personne' : 'personnes'}.`,
      ];
      return pool[Number(v.index) % pool.length];
    },
    pt: (v) => {
      const pool = [
        `Você expressou algo significativo para ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'pessoa' : 'pessoas'} este mês.`,
        `Sua Árvore Viva cresceu para ${v.total} ${v.total === 1 ? 'momento' : 'momentos'}.`,
        `Você escreveu mais mensagens para ${v.topRelationship}.`,
        `Faz ${v.daysSinceLast} ${v.daysSinceLast === 1 ? 'dia' : 'dias'} desde sua última mensagem.`,
        `Você compartilhou gratidão com ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'pessoa' : 'pessoas'}.`,
      ];
      return pool[Number(v.index) % pool.length];
    },
    ar: (v) => {
      const pool = [
        `لقد عبّرت عن شيء ذي معنى لـ ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'شخص' : 'أشخاص'} هذا الشهر.`,
        `شجرتك الحية نمت إلى ${v.total} ${v.total === 1 ? 'لحظة' : 'لحظات'}.`,
        `كتبت أكبر عدد من الرسائل لـ ${v.topRelationship}.`,
        `مضى ${v.daysSinceLast} ${v.daysSinceLast === 1 ? 'يوم' : 'أيام'} منذ رسالتك الأخيرة.`,
        `شاركت الامتنان مع ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'شخص' : 'أشخاص'}.`,
      ];
      return pool[Number(v.index) % pool.length];
    },
    ja: (v) => {
      const pool = [
        `今月${v.uniqueRecipients}人に大切な気持ちを伝えました。`,
        `リビングツリーが${v.total}の瞬間に成長しました。`,
        `${v.topRelationship}に最も多くのメッセージを書きました。`,
        `最後のメッセージから${v.daysSinceLast}日が経ちました。`,
        `${v.uniqueRecipients}人に感謝を共有しました。`,
      ];
      return pool[Number(v.index) % pool.length];
    },
    ko: (v) => {
      const pool = [
        `이번 달 ${v.uniqueRecipients}명에게 의미 있는 마음을 전했습니다.`,
        `리빙 트리가 ${v.total}개의 순간으로 성장했습니다.`,
        `${v.topRelationship}에게 가장 많은 메시지를 보냈습니다.`,
        `마지막 메시지로부터 ${v.daysSinceLast}일이 지났습니다.`,
        `${v.uniqueRecipients}명에게 감사를 전했습니다.`,
      ];
      return pool[Number(v.index) % pool.length];
    },
    id: (v) => {
      const pool = [
        `Anda telah mengungkapkan sesuatu yang bermakna kepada ${v.uniqueRecipients} orang bulan ini.`,
        `Pohon Hidup Anda telah tumbuh menjadi ${v.total} momen.`,
        `Anda paling banyak menulis pesan untuk ${v.topRelationship}.`,
        `Sudah ${v.daysSinceLast} hari sejak pesan terakhir Anda.`,
        `Anda telah berbagi rasa syukur dengan ${v.uniqueRecipients} orang.`,
      ];
      return pool[Number(v.index) % pool.length];
    },
    hi: (v) => {
      const pool = [
        `आपने इस महीने ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'व्यक्ति' : 'लोगों'} को कुछ सार्थक व्यक्त किया।`,
        `आपका जीवित पेड़ ${v.total} ${v.total === 1 ? 'पल' : 'पलों'} तक बढ़ गया है।`,
        `आपने ${v.topRelationship} को सबसे अधिक संदेश लिखे हैं।`,
        `आपके अंतिम संदेश के बाद ${v.daysSinceLast} ${v.daysSinceLast === 1 ? 'दिन' : 'दिन'} हो गए हैं।`,
        `आपने ${v.uniqueRecipients} ${v.uniqueRecipients === 1 ? 'व्यक्ति' : 'लोगों'} के साथ आभार साझा किया।`,
      ];
      return pool[Number(v.index) % pool.length];
    },
  };

  const gen = insightTemplates[language] || insightTemplates.en;
  const vars = {
    total,
    uniqueRecipients: uniqueRecipients.size,
    monthlyCount: thisMonth.length,
    topRelationship: topRelationship?.[0] || 'friends',
    daysSinceLast,
  };

  for (let i = 0; i < 5; i++) {
    insights.push(gen({ ...vars, index: i }));
  }

  return insights;
}

export function MomentsThatMatterPanel({ announcements }: MomentsThatMatterPanelProps) {
  const { language } = useLanguage();
  const { trackEvent } = useEventTracking();
  const copy = lc(panelCopy, language);

  const published = useMemo(
    () => announcements.filter((a) => a.status === 'approved'),
    [announcements]
  );

  const insights = useMemo(() => generateInsights(published, language), [published, language]);
  const [insightIndex, setInsightIndex] = useState(0);

  // Rotate insight every 10 seconds
  useEffect(() => {
    if (insights.length <= 1) return;
    const timer = setInterval(() => {
      setInsightIndex((i) => (i + 1) % insights.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [insights.length]);

  // Track view
  useEffect(() => {
    trackEvent('insight_panel_viewed', { has_published: published.length > 0 });
  }, []);

  const handleCtaClick = (action: string) => {
    trackEvent('insight_cta_clicked', { action });
  };

  const isEmpty = published.length === 0;
  const currentInsight = isEmpty ? copy.starterInsight : insights[insightIndex] || '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.15 }}
      className="mb-8"
    >
      <Card className="relative overflow-hidden border-0 rounded-2xl glass">
        {/* Emerald gradient edge */}
        <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-primary/60 via-primary/30 to-primary/10 rounded-l-2xl" />
        {/* Subtle glow */}
        <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-primary/5 blur-[40px] pointer-events-none" />

        <CardContent className="p-6 md:p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground tracking-tight pt-1">
              {copy.title}
            </h3>
          </div>

          <motion.p
            key={insightIndex}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4 }}
            className="text-muted-foreground text-base leading-relaxed mb-6 pl-12"
          >
            {currentInsight}
          </motion.p>

          <div className="flex flex-wrap gap-3 pl-12">
            <Link to="/create" onClick={() => handleCtaClick('write_message')}>
              <Button size="lg" className="gap-2 rounded-2xl shadow-lg shadow-primary/20">
                <Pen className="h-4 w-4" />
                {isEmpty ? copy.writeFirst : copy.writeMessage}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
